import { DefaultSectionCopy, Section } from "../providers/CopyProvider"
import { TenantButtonCopy, TenantTextCopy } from "@hydrologiq/gql/graphql"
import { COPY_KEYS, DEFAULT_COPY_MAPPINGS } from "./constants"

/**
 * Helper to create standardized copy configurations
 */
export function createSectionCopy(config: {
  section: Section
  name?: string
  base?: string
  customHeadings?: Array<{ key: string; value: string }>
  customButtons?: Array<{ key: string; label: string; description?: string }>
}): DefaultSectionCopy {
  const { section, name, base, customHeadings = [], customButtons = [] } = config

  // Default headings that most sections need
  const defaultHeadings: TenantTextCopy[] = [
    { name: COPY_KEYS.HEADINGS.NAME, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NAME] },
    { name: COPY_KEYS.HEADINGS.ACTIONS, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ACTIONS] },
  ]

  // Default buttons that most sections need
  const defaultButtons: TenantButtonCopy[] = [
    {
      name: COPY_KEYS.BUTTONS.CREATE,
      label: `Create ${name || section}`,
      description: `Add a new ${name?.toLowerCase() || section} to the system`,
    },
  ]

  return {
    type: section,
    name: name || section.charAt(0).toUpperCase() + section.slice(1),
    base: base || section.toLowerCase(),
    routes: [],
    buttons: [
      ...defaultButtons,
      ...customButtons.map((btn) => ({ name: btn.key, label: btn.label, description: btn.description })),
    ],
    headings: [...defaultHeadings, ...customHeadings.map((heading) => ({ name: heading.key, value: heading.value }))],
  }
}

/**
 * Helper to get common heading configurations for specific use cases
 */
export const COMMON_HEADING_SETS = {
  TABLE_BASIC: [
    { key: COPY_KEYS.HEADINGS.NAME, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NAME] },
    { key: COPY_KEYS.HEADINGS.TYPE, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.TYPE] },
    { key: COPY_KEYS.HEADINGS.STATUS, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.STATUS] },
    { key: COPY_KEYS.HEADINGS.ACTIONS, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ACTIONS] },
  ],

  ASSETS_TABLE: [
    { key: COPY_KEYS.HEADINGS.NAME, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.NAME] },
    { key: COPY_KEYS.HEADINGS.TYPE, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.TYPE] },
    { key: COPY_KEYS.HEADINGS.STATUS, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.STATUS] },
    { key: COPY_KEYS.HEADINGS.SITE, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.SITE] },
    { key: COPY_KEYS.HEADINGS.LOCATION, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.LOCATION] },
    { key: COPY_KEYS.HEADINGS.ACTIONS, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ACTIONS] },
  ],

  CUSTOMERS_TABLE: [
    { key: COPY_KEYS.HEADINGS.NAME, value: "Customer Name" },
    { key: COPY_KEYS.HEADINGS.SITE, value: "Site(s)" },
    { key: COPY_KEYS.HEADINGS.ASSET, value: "Asset(s)" },
    { key: COPY_KEYS.HEADINGS.USAGE, value: "Total hydrogen usage" },
    { key: COPY_KEYS.HEADINGS.ACTIONS, value: DEFAULT_COPY_MAPPINGS[COPY_KEYS.HEADINGS.ACTIONS] },
  ],
} as const

/**
 * Type-safe helper for creating copy mappings
 */
export function createCopyMappings<T extends Record<string, string>>(mappings: T): T {
  return mappings
}

/**
 * Helper functions for composing messages from copy keys
 */

/**
 * Compose a success toast title for entity creation
 * Example: "Fill site created" or "Order created"
 */
export function composeCreatedTitle(copy: Record<string, string>, entityKey: string): string {
  return `${copy[entityKey]} ${copy[COPY_KEYS.MESSAGES.CREATED]}`
}

/**
 * Compose a success toast description for entity creation
 * Example: 'Fill site "My Site" has been successfully created'
 */
export function composeCreatedDescription(copy: Record<string, string>, entityKey: string, entityName: string): string {
  return `${copy[entityKey]} "${entityName}" ${copy[COPY_KEYS.MESSAGES.SUCCESSFULLY_CREATED]}.`
}

/**
 * Compose a success toast title for entity update
 * Example: "Fill site updated" or "Order updated"
 */
export function composeUpdatedTitle(copy: Record<string, string>, entityKey: string): string {
  return `${copy[entityKey]} ${copy[COPY_KEYS.MESSAGES.UPDATED]}`
}

/**
 * Compose a success toast description for entity update
 * Example: 'Fill site "My Site" has been successfully updated'
 */
export function composeUpdatedDescription(copy: Record<string, string>, entityKey: string, entityName: string): string {
  return `${copy[entityKey]} "${entityName}" ${copy[COPY_KEYS.MESSAGES.SUCCESSFULLY_UPDATED]}.`
}

/**
 * Compose a success toast title for entity deletion
 * Example: "Fill site deleted" or "Order deleted"
 */
export function composeDeletedTitle(copy: Record<string, string>, entityKey: string): string {
  return `${copy[entityKey]} ${copy[COPY_KEYS.MESSAGES.DELETED]}`
}

/**
 * Compose a success toast description for entity deletion
 * Example: 'Fill site "My Site" has been successfully deleted'
 */
export function composeDeletedDescription(copy: Record<string, string>, entityKey: string, entityName: string): string {
  return `${copy[entityKey]} "${entityName}" ${copy[COPY_KEYS.MESSAGES.SUCCESSFULLY_DELETED]}.`
}

/**
 * Compose a loading toast for entity creation
 * Example: "Creating Fill site" with description "Adding Fill site to system..."
 */
export function composeCreatingToast(copy: Record<string, string>, entityKey: string): { title: string; description: string } {
  return {
    title: `${copy[COPY_KEYS.ACTIONS.CREATING]} ${copy[entityKey]}`,
    description: `${copy[COPY_KEYS.MESSAGES.ADDING_TO_SYSTEM]}`,
  }
}

/**
 * Compose form helper text
 * Example: "Please provide fill site information below." or "Please update fill site information below."
 */
export function composeFormHelperText(copy: Record<string, string>, entityKey: string, actionType: "create" | "edit" = "create"): string {
  const helperTextKey = actionType === "create" ? COPY_KEYS.FORMS.HELPER_TEXT_CREATE : COPY_KEYS.FORMS.HELPER_TEXT_EDIT
  if (copy[helperTextKey]) {
    return copy[helperTextKey].replace("{entity}", copy[entityKey].toLowerCase())
  }
  // Fallback for backwards compatibility
  return `${copy[COPY_KEYS.FORMS.PLEASE_PROVIDE]} ${copy[entityKey].toLowerCase()} ${copy[COPY_KEYS.FORMS.INFORMATION_BELOW]}.`
}

/**
 * Compose form heading for creation
 * Example: "Create a fill site"
 */
export function composeCreateHeading(copy: Record<string, string>, entityKey: string): string {
  return `${copy[COPY_KEYS.BUTTONS.CREATE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[entityKey].toLowerCase()}`
}

/**
 * Compose validation error for missing field
 * Example: "Please provide a fill site name"
 */
export function composeMissingFieldError(copy: Record<string, string>, fieldKey: string): string {
  return `${copy[COPY_KEYS.FORMS.PLEASE_PROVIDE]} ${copy[COPY_KEYS.COMMON.A]} ${copy[fieldKey].toLowerCase()}`
}

/**
 * Compose placeholder text for input
 * Example: "Enter fill site name"
 */
export function composeInputPlaceholder(copy: Record<string, string>, fieldKey: string): string {
  return `${copy[COPY_KEYS.FORMS.ENTER]} ${copy[fieldKey].toLowerCase()}`
}

/**
 * Compose placeholder text with entity replacement
 * Example: "Enter site name" or "Select asset type..."
 */
export function composePlaceholder(copy: Record<string, string>, placeholderKey: string, entityKey?: string): string {
  if (entityKey && copy[placeholderKey]) {
    return copy[placeholderKey].replace("{entity}", copy[entityKey].toLowerCase())
  }
  return copy[placeholderKey] || ""
}

/**
 * Compose validation error message
 * Example: "Please provide a name for the site"
 */
export function composeValidationMessage(copy: Record<string, string>, messageKey: string, entityKey?: string, missingFields?: string[]): string {
  let message = copy[messageKey] || ""
  if (entityKey) {
    message = message.replace("{entity}", copy[entityKey].toLowerCase())
  }
  if (missingFields && missingFields.length > 0) {
    message = `${message} ${missingFields.join(", ")}`
  }
  return message
}
